import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * #672 — the vendor-WO intake classification, visible and correctable.
 *
 * `work_order_briefing.service_classification.service_type` steers trade-keyed
 * behavior (completion gates; the WO fill overlay pre-#669) but was invisible
 * and uneditable — the office found a "Backflow Test" job silently classified
 * `fire_extinguisher_inspection` and had no way to even see it, let alone fix
 * it. This chip shows the label wherever a vendor-WO job surfaces (job page
 * vitals strip, Fix-mapping banner) and, for the office, edits it through the
 * audited PATCH.
 */

/** Mirrors the server enum in routes/jobServiceClassification.ts. */
export const SERVICE_TYPE_LABEL: Record<string, string> = {
    backflow_inspection: "Backflow inspection",
    backflow_repair: "Backflow repair",
    fire_extinguisher_inspection: "Fire extinguisher inspection",
    fire_suppression: "Fire suppression",
    fire_alarm: "Fire alarm",
    proposal_request: "Proposal request",
    other: "Other",
};

export function serviceTypeLabel(serviceType: string): string {
    return SERVICE_TYPE_LABEL[serviceType] ?? serviceType.replace(/_/g, " ");
}

export function ServiceClassificationChip({
    jobId,
    serviceType,
    canEdit,
    mismatch = false,
    onSaved,
}: {
    jobId: string;
    serviceType: string | null | undefined;
    canEdit: boolean;
    /** The job carries backflow evidence that contradicts a non-backflow label
     *  (#669's fill warning) — render loud so the office fixes the label. */
    mismatch?: boolean;
    /** Extra invalidation hook for surfaces with their own query keys. */
    onSaved?: () => void;
}) {
    const [editing, setEditing] = useState(false);
    const qc = useQueryClient();
    const save = useMutation({
        mutationFn: (next: string) =>
            api.patch<{ ok: boolean; serviceType: string }>(`/api/jobs/${jobId}/service-classification`, {
                serviceType: next,
            }),
        onSuccess: (r) => {
            void qc.invalidateQueries({ queryKey: ["job", jobId] });
            onSaved?.();
            setEditing(false);
            toast.success(`Classification changed to ${serviceTypeLabel(r.serviceType)} (audited).`);
        },
        onError: (e: unknown) => {
            const msg =
                (e as { body?: { message?: string; error?: string } })?.body?.message ??
                (e as { body?: { error?: string } })?.body?.error ??
                "unknown error";
            toast.error(`Couldn't change classification: ${msg}`);
        },
    });

    if (!serviceType) return null;

    if (editing && canEdit) {
        return (
            <span className="inline-flex items-center gap-1" data-testid="service-classification-edit">
                <select
                    autoFocus
                    className="rounded border bg-background px-1 py-0.5 text-[11px]"
                    defaultValue={serviceType}
                    disabled={save.isPending}
                    onChange={(e) => {
                        if (e.target.value !== serviceType) save.mutate(e.target.value);
                        else setEditing(false);
                    }}
                    onBlur={() => !save.isPending && setEditing(false)}
                    aria-label="Change the intake service classification"
                >
                    {Object.entries(SERVICE_TYPE_LABEL).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
            </span>
        );
    }

    const tone = mismatch
        ? "border-amber-400/70 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
        : "border-border bg-muted text-foreground/70";
    const title = mismatch
        ? `Intake classified this work order "${serviceTypeLabel(serviceType)}", but the job has backflow test results — the label steers completion gates, so fix it if it's wrong.${canEdit ? " Click to change (audited)." : ""}`
        : `How intake classified this work order. The label steers trade-keyed behavior.${canEdit ? " Click to change (audited)." : ""}`;
    const body = (
        <>
            {mismatch && <span aria-hidden>⚠</span>}
            Classified: {serviceTypeLabel(serviceType)}
        </>
    );
    if (!canEdit) {
        return (
            <span
                data-testid="service-classification-chip"
                title={title}
                className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}
            >
                {body}
            </span>
        );
    }
    return (
        <button
            type="button"
            data-testid="service-classification-chip"
            title={title}
            onClick={() => setEditing(true)}
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide hover:border-primary/60 ${tone}`}
        >
            {body}
        </button>
    );
}
